/**
 * Single source for all UI texts (BLUEPRINT E16). German is the reference language; every enabled
 * language must have exactly the same keys (packages/i18n/test/parity.test.ts). Apps import only from here.
 */
import { PROJECT, euro, fmtDate, fmtRange, type ErrorCode, type ErrorParams } from '@immoaktas/shared';
import deCommon from '../locales/de/common.json';
import deErrors from '../locales/de/errors.json';
import deWeb from '../locales/de/web.json';
import deAdmin from '../locales/de/admin.json';
import deMobile from '../locales/de/mobile.json';
import enCommon from '../locales/en/common.json';
import enErrors from '../locales/en/errors.json';
import enWeb from '../locales/en/web.json';
import enAdmin from '../locales/en/admin.json';
import enMobile from '../locales/en/mobile.json';

export const LANGUAGES = PROJECT.languages.enabled as readonly Language[];
export type Language = 'de' | 'en';
export const DEFAULT_LANGUAGE = PROJECT.languages.default as Language;
export const NAMESPACES = ['common', 'errors', 'web', 'admin', 'mobile'] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const resources = {
  de: { common: deCommon, errors: deErrors, web: deWeb, admin: deAdmin, mobile: deMobile },
  en: { common: enCommon, errors: enErrors, web: enWeb, admin: enAdmin, mobile: enMobile },
} as const;

/**
 * Version of the consent text `common:consumer.withdrawalConsent` (§ 356 Abs. 4/5 BGB, docs/BILLING.md §12). Change it
 * whenever the text changes: the server stores it with every consent and quotes the text in the contract confirmation.
 */
export const WITHDRAWAL_CONSENT_VERSION = '2026-10-24';

/** Intl locale per language (dates, numbers, currency). */
export const LOCALE: Record<Language, string> = { de: 'de-DE', en: 'en-GB' };

/** Picks the first supported language from e.g. navigator.languages or expo-localization tags. */
export function detectLanguage(candidates: readonly (string | null | undefined)[]): Language {
  for (const c of candidates) {
    const base = c?.toLowerCase().split(/[-_]/)[0];
    if (base && (LANGUAGES as readonly string[]).includes(base)) return base as Language;
  }
  return DEFAULT_LANGUAGE;
}

/** i18next init options shared by web, admin and mobile (only the app namespace differs). */
export function i18nOptions(lng: Language, appNs: 'web' | 'admin' | 'mobile') {
  return {
    resources,
    lng,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: [...LANGUAGES],
    ns: ['common', 'errors', appNs],
    defaultNS: appNs,
    fallbackNS: 'common',
    interpolation: { escapeValue: false },
    returnNull: false,
  } as const;
}

/** Translation key for an API error code; clients never show server sentences (B8). */
export const errorKey = (code: ErrorCode | string | undefined) => (code ? `errors:codes.${code}` : 'errors:generic');

/** Keys of specific server messages (errors.json `server`). The server builds them with msg(key, params). */
export type ServerErrorKey = keyof (typeof resources)['de']['errors']['server'];
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
/** German text of a server message (the API's backwards-compatible `message`); ISO dates as TT.MM.JJJJ. */
export function serverErrorDe(key: ServerErrorKey, params: ErrorParams = {}): string {
  return (resources.de.errors.server[key] as string).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k: string) => {
    const v = params[k];
    return v === undefined ? '' : typeof v === 'string' && ISO_DAY.test(v) ? v.split('-').reverse().join('.') : String(v);
  });
}
/** Client text of a server message (API error or import row): errors:server.<key> with localized dates, else `fallback`. */
export function serverMessageText(t: (key: string, opts?: Record<string, unknown>) => string, m: { key?: string; params?: ErrorParams }, fallback: string): string {
  if (!m.key) return fallback;
  const params = Object.fromEntries(Object.entries(m.params ?? {}).map(([k, v]) => [k, typeof v === 'string' && ISO_DAY.test(v) ? fmtDate(v) : v]));
  return t(`errors:server.${m.key}`, { ...params, defaultValue: fallback });
}
/** Client text of an API error: the specific server message if the client knows its key, else the text of the code. */
export const apiErrorText = (t: (key: string, opts?: Record<string, unknown>) => string, e: { code?: string; key?: string; params?: ErrorParams }) =>
  serverMessageText(t, e, t(errorKey(e.code), { defaultValue: t('errors:generic') }));

type TFn = (key: string, opts?: Record<string, unknown>) => string;
/** "Nachzahlung 12,00 €" / "Amount due €12.00": positive cents = tenant owes. */
export const balanceText = (t: TFn, cents: number) =>
  cents > 0 ? t('common:balance.due', { amount: euro(cents) }) : cents < 0 ? t('common:balance.credit', { amount: euro(-cents) }) : t('common:balance.even');
/** Date range with translated open end. */
export const rangeText = (t: TFn, from: string, to: string | null) => fmtRange(from, to, t('common:open'));

/** Labels for shared enums (cost categories, unit kinds, roles …). Server PDFs use enumLabelDe (legal docs are German). */
export type EnumGroup = keyof (typeof resources)['de']['common']['enums'];
export const enumLabel = (t: TFn, group: EnumGroup, value: string) => t(`common:enums.${group}.${value}`);
export const enumLabelDe = (group: EnumGroup, value: string) => (resources.de.common.enums[group] as Record<string, string>)[value] ?? value;
